/**
 * Redaction rules — full phone numbers and secret-shaped strings never leave
 * the process boundary: the ring buffer, NDJSON files, and the stderr mirror
 * only ever see a last-four suffix or "[redacted]".
 *
 * Lifted from EQStack's voice-mcp (domain/redact.ts), which enforced the same
 * invariant at its config boundary. One deviation: `redactValue` carries a
 * cycle guard, because here it runs on the logger's hot path where the
 * never-throw invariant applies and caller-supplied data can be circular.
 */
/** E.164-ish: "+" followed by 7–15 digits, optionally spaced/hyphenated. */
const PHONE_RE = /\+\d[\d\s\-().]{5,17}\d/g;
/**
 * Email-ish: something, an `@`, a dotted host. Deliberately UNANCHORED, because
 * a redaction rule has to find addresses inside prose.
 *
 * That is also why email redaction is OFF by default — see `RedactOptions`.
 */
const EMAIL_RE = /[^\s@<>()[\]{},;:"']+@[^\s@<>()[\]{},;:"']+\.[^\s@<>()[\]{},;:"']+/g;
/** Bearer/API-key shapes known to circulate in MCP stacks. */
const SECRET_RE = /\b(sk-[A-Za-z0-9-]{10,}|github_pat_[A-Za-z0-9_]{10,}|gh[pousr]_[A-Za-z0-9]{10,}|SK[a-f0-9]{32}|AC[a-f0-9]{32})\b/g;
export function lastFour(number) {
    const digits = number.replace(/\D/g, "");
    return digits.slice(-4);
}
/**
 * Mask one email address: first character of the local part, then the domain.
 *
 * `george.x@gmail.com` → `g…@gmail.com`; a local part of 2 characters or fewer
 * becomes `…@gmail.com`.
 *
 * **Not the `lastFour` shape, deliberately.** Keeping first AND last would
 * mirror the phone rule, but the analogy breaks at the short end: `lastFour`
 * shows 4 of a phone's 7–15 digits and can never disclose the whole number,
 * whereas first+last of a two-character local part IS the whole local part —
 * `al@x.com` → `a…l@x.com` discloses the address it was asked to hide.
 * Raised by the EQStack session against the first proposal, and correct.
 *
 * **The domain is preserved on purpose.** It is the half that makes a log line
 * diagnostic ("the failure is on gmail.com addresses") and rarely the half that
 * identifies a person.
 *
 * Exported so a consumer can apply it at a boundary it KNOWS carries addresses
 * — an API error object quoting a recipient — without turning on the global
 * rule and its false positives.
 */
export function redactEmail(address) {
    const at = address.lastIndexOf("@");
    if (at <= 0)
        return address;
    const local = address.slice(0, at);
    const domain = address.slice(at);
    return local.length <= 2 ? `…${domain}` : `${local[0]}…${domain}`;
}
/**
 * Redact a string: phone numbers become "…NNNN", secret shapes become
 * "[redacted]", and — only with `{ emails: true }` — addresses are masked.
 */
export function redactString(input, options = {}) {
    const out = input.replace(PHONE_RE, (m) => `…${lastFour(m)}`).replace(SECRET_RE, "[redacted]");
    return options.emails ? out.replace(EMAIL_RE, (m) => redactEmail(m)) : out;
}
/**
 * Deep-redact any JSON-ish value. Non-serializable leaves pass through
 * untouched; circular references become "[circular]" instead of recursing.
 */
export function redactValue(value, options = {}) {
    return redactInner(value, new WeakSet(), options);
}
// `seen` holds the current ancestor path, not everything visited: a diamond
// (the same object referenced twice without a cycle) survives, matching what
// JSON.stringify would accept.
function redactInner(value, seen, options) {
    if (typeof value === "string")
        return redactString(value, options);
    if (Array.isArray(value)) {
        if (seen.has(value))
            return "[circular]";
        seen.add(value);
        const out = value.map((v) => redactInner(v, seen, options));
        seen.delete(value);
        return out;
    }
    if (value && typeof value === "object") {
        if (seen.has(value))
            return "[circular]";
        seen.add(value);
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = redactInner(v, seen, options);
        }
        seen.delete(value);
        return out;
    }
    return value;
}
//# sourceMappingURL=redact.js.map